import { buildCompletedSurvey } from '@chiroup/core/functions/completedSurveysForNotes';
import { Visit } from '@chiroup/core/types/PatientVisit.type';
import dayjs from 'dayjs';
import { useSandboxClinicalData } from '../../../../../contexts/sandboxClinicalData.context';
import WalkthroughScheduling from '../components/WalkthroughScheduling';
// The shared icon set's `check` is a thin circled check and `home` is a
// pulse glyph, so the completion screen uses lucide for the design's bold
// solid-weight icons.
import { Check, Clock, FileText, Home, Layers, Zap } from 'lucide-react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MeContext } from '../../../../../contexts/me.context';
import useEvaluations from '../../../../../hooks/useEvaluations';
import useModalities from '../../../../../hooks/useModalities';
import useMuscles from '../../../../../hooks/useMuscles';
import useSupplements from '../../../../../hooks/useSupplements';
import useTests from '../../../../../hooks/useTests';
import useTreatments from '../../../../../hooks/useTreatments';
import { CustomizationListItem } from '../../../../clinical-skills/CustomizationListItem.type';
import { TiptapEditor } from '../../../../common/tiptap/TiptapEditor';
import { getWYSIWYGAssessmentContentFn } from '../../../../patients/detail/visits/helpers/assessmentStrBuilder';
import { getWYSIWYGPlanContent } from '../../../../patients/detail/visits/helpers/planStrBuilder';
import { getWYSIWYGSubjectiveContentFnV2 } from '../../../../patients/detail/visits/helpers/subjectiveStrBuilder';
import { getUnifiedObjectiveContentFn } from '../../../../patients/detail/visits/helpers/unifiedObjectiveStrBuilder';
import useComplaintConditions from '../../../../patients/hooks/useComplaintConditions';
import { ConfettiBurst } from '../../ConfettiBurst';
import WalkthroughCallout from '../components/WalkthroughCallout';
import { SANDBOX_COMPLETED_SURVEYS, SANDBOX_PATIENT } from '../fixtures/anitaBack';
import {
  COMPLETED_NOTE_CALLOUT,
  COMPLETION_SCREEN,
  OAP_COPY,
  VOICE_TO_CHART_COPY,
} from '../sandboxCopy';
import {
  applyAssessmentCodeFallback,
  applyJointDysfunctionProse,
  applyMyofascialProtocolFallback,
  applySandboxPlanTemplates,
  stripSurveyAnswerColors,
  tidySurveySections,
} from '../sandboxNote';

interface ReviewSignStepProps {
  visitForm: Visit;
  signed: boolean;
  completed: boolean;
  onSign: () => void;
  /** Returns to the Plan beat (requirement 2026-09-28); unsigned view only —
   *  a signed note is terminal. */
  onBack: () => void;
  onContinue: () => void;
  onExit: () => void;
}

const stripTags = {
  br: (str: string) => str.replace(/<\/br>|<br\s*\/>/g, ''),
  p: (str: string) => str.replace(/<p><\/p>|<\/p>\n?/g, ''),
};

/** Static confetti decorating the completion header, per the provided
 *  design: dense pill-shaped pieces in greens, navy, and gray scattered
 *  across the top and down the upper sides. Fixed positions keep the layout
 *  deterministic. */
const COMPLETION_CONFETTI = [
  'left-[3%] top-24 h-2 w-7 -rotate-[20deg] rounded-full bg-primary-400',
  'left-[6%] top-10 h-2 w-6 rotate-[15deg] rounded-full bg-primary-600',
  'left-[9%] top-40 h-2.5 w-2.5 rounded-full bg-gray-400 dark:bg-gray-500',
  'left-[13%] top-5 h-2 w-7 -rotate-[35deg] rounded-full bg-slate-700 dark:bg-gray-300',
  'left-[16%] top-28 h-2 w-2 rounded-full bg-primary-300',
  'left-[21%] top-14 h-1.5 w-5 rotate-[60deg] rounded-full bg-primary-500',
  'left-[27%] top-32 h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-500',
  'left-[31%] top-6 h-2 w-6 rotate-[30deg] rounded-full bg-primary-400',
  'left-[37%] top-20 h-1.5 w-5 -rotate-[50deg] rounded-full bg-primary-300',
  'left-[42%] top-8 h-2 w-2 rounded-full bg-primary-600',
  'left-[47%] top-32 h-1.5 w-4 rotate-[20deg] rounded-full bg-gray-400 dark:bg-gray-500',
  'right-[3%] top-20 h-2 w-7 rotate-[25deg] rounded-full bg-primary-400',
  'right-[6%] top-8 h-2 w-6 -rotate-[15deg] rounded-full bg-primary-600',
  'right-[9%] top-40 h-2.5 w-2.5 rounded-full bg-gray-400 dark:bg-gray-500',
  'right-[13%] top-4 h-2 w-7 rotate-[35deg] rounded-full bg-slate-700 dark:bg-gray-300',
  'right-[17%] top-28 h-2 w-2 rounded-full bg-primary-300',
  'right-[22%] top-12 h-1.5 w-5 -rotate-[60deg] rounded-full bg-primary-500',
  'right-[27%] top-32 h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-500',
  'right-[32%] top-6 h-2 w-6 -rotate-[30deg] rounded-full bg-primary-400',
  'right-[38%] top-22 h-1.5 w-5 rotate-[45deg] rounded-full bg-primary-300',
  'right-[43%] top-10 h-2 w-2 rounded-full bg-primary-600',
  'right-[48%] top-36 h-1.5 w-4 -rotate-[25deg] rounded-full bg-slate-600 dark:bg-gray-400',
];

/** One icon per COMPLETION_SCREEN.cards entry, in order. */
const COMPLETION_CARD_ICONS = [
  <FileText key="doc" className="h-6 w-6" aria-hidden />,
  <Layers key="layers" className="h-6 w-6" aria-hidden />,
  <Zap key="bolt" className="h-6 w-6" aria-hidden />,
];

/**
 * Step 4 — Review & Sign. The note is generated from the prospect's actual
 * walkthrough selections through the SAME string builders the EHR uses
 * (subjective / unified objective / assessment / plan), with one
 * sandbox-specific rule from the spec: Myofascial falls back to the condition
 * protocols only when the O module has no muscle selections. The review view
 * IS the real SOAP step's editor stack (TiptapEditor per section), fully
 * editable, minus Clear per the spec. Sign Note → completed Daily Note +
 * confetti, with the walkthrough callout carrying Continue → Charting
 * Completion (built to the provided design, 2026-09-28).
 */
const ReviewSignStep: React.FC<ReviewSignStepProps> = ({
  visitForm,
  signed,
  completed,
  onSign,
  onBack,
  onContinue,
  onExit,
}) => {
  const sandbox = useSandboxClinicalData();
  const { me } = useContext(MeContext);
  const [confetti, setConfetti] = useState(false);

  const complaints = useMemo(
    () => visitForm.encounterInfo?.complaints || [],
    [visitForm.encounterInfo?.complaints]
  );

  const conditionIds = useMemo(
    () =>
      complaints.flatMap(
        complaint => complaint.assessment?.conditions?.map(c => c.conditionId) || []
      ),
    [complaints]
  );

  const { data: evals } = useEvaluations({ refetchOnMount: false });
  const { data: muscles } = useMuscles({ refetchOnMount: false });
  const { data: testsData } = useTests({
    refetchOnMount: false,
    ids: Object.keys(visitForm.plan?.imagingAndTests || {}).map(item => parseInt(item)),
  });
  const { data: modalitiesData } = useModalities({
    refetchOnMount: false,
    ids: Object.keys(visitForm.plan?.modalities || {}).map(item => parseInt(item, 10)),
  });
  const { data: supplementsData } = useSupplements({
    refetchOnMount: false,
    ids: Object.keys(visitForm.plan?.supplementsAndSupplies || {}).map(item => parseInt(item)),
  });
  const { data: treatmentsData } = useTreatments({
    type: 'Clinical',
    hide: false,
    refetchOnMount: false,
  });
  const { allConditionsRetrieved } = useComplaintConditions(conditionIds, true);

  // Same generation recipe as the real wizard's "continue to SOAP" step
  // (StepperNavV2), minus the additional-surveys append the sandbox doesn't
  // carry.
  const note = useMemo(() => {
    const activeComplaintsProblemAreas = complaints.reduce(
      (obj: { [key: string]: boolean }, complaint) => {
        if (complaint.id) obj[complaint.id] = true;

        return obj;
      },
      {}
    );

    // Completed intake surveys append after the CCS content, mirroring the
    // real wizard's surveysToDocument pass (tester note #24), in a single
    // color (round-2 note #9) with tidied system sections (round-3 note #4).
    const completedSurveysHtml = stripSurveyAnswerColors(
      tidySurveySections(
        SANDBOX_COMPLETED_SURVEYS.map(survey => buildCompletedSurvey(survey)).join('')
      )
    );

    const subjective = stripTags.p(
      stripTags.br(
        getWYSIWYGSubjectiveContentFnV2({
          complaints,
          totalComplaints: complaints.length,
          complaintSummaries: visitForm.complaintSummaries,
          activeComplaintsProblemAreas,
          complaintNames: complaints.reduce(
            (obj: { [key: string]: { name: string; order: number } }, complaint, index) => {
              if (complaint.id && complaint.complaint) {
                obj[complaint.id] = { name: complaint.complaint, order: index };
              }

              return obj;
            },
            {}
          ),
        })() + completedSurveysHtml
      )
    );

    const assessment = stripTags.br(
      getWYSIWYGAssessmentContentFn({
        // Backfills ICD codes the library list may not have carried, so the
        // Assessment always prints them (round-3 note #6).
        complaints: applyAssessmentCodeFallback(complaints, allConditionsRetrieved || {}),
        totalComplaints: complaints.length,
        allConditionsRetrieved: allConditionsRetrieved || {},
        currentVisit: 1,
        totalVisits: 0,
        encounterInfo: visitForm.encounterInfo,
        reevaluation: false,
      })()
    );

    const plan = stripTags.br(
      getWYSIWYGPlanContent({
        visitForm: applySandboxPlanTemplates(visitForm),
        modalitiesData: modalitiesData || { data: [] },
        testsData: testsData || { data: [] },
        supplementsData: supplementsData || { data: [], itemTaxHash: {} },
        treatmentsData: treatmentsData || { data: [] },
        muscles: muscles || { data: [] },
      })
    );

    const objective = stripTags.br(
      getUnifiedObjectiveContentFn(
        applyJointDysfunctionProse(
          applyMyofascialProtocolFallback(visitForm, allConditionsRetrieved || {})
        ),
        muscles || { data: [] },
        (evals || { data: [] }) as unknown as { data: CustomizationListItem[] }
      ) || ''
    );

    return { subjective, objective, assessment, plan };
  }, [
    complaints,
    visitForm,
    allConditionsRetrieved,
    modalitiesData,
    testsData,
    supplementsData,
    treatmentsData,
    muscles,
    evals,
  ]);

  // Editable working copy behind the real Tiptap editors ("Review. Refine.
  // Sign."). Re-seeded only when the GENERATED content changes (e.g. the
  // muscle/evaluation lists resolve after mount). Two constraints here:
  // `note`'s identity churns every render (useComplaintConditions rebuilds
  // its map inline), so an unconditional set is an infinite update loop; and
  // comparing against `draft` instead of the last seed would wipe the
  // prospect's manual refinements on any rerender.
  const [draft, setDraft] = useState(note);
  // The VoiceNote Summary section the real SOAP step opens with, seeded with
  // the Voice-to-Chart demonstration's captured Subjective so the Step 1 demo
  // pays off here. Editable like the rest; no Clear control (spec).
  const [voiceNote, setVoiceNote] = useState(VOICE_TO_CHART_COPY.subjectiveText);
  const lastSeededNote = useRef(note);
  useEffect(() => {
    const seeded = lastSeededNote.current;
    const regenerated =
      seeded.subjective !== note.subjective ||
      seeded.objective !== note.objective ||
      seeded.assessment !== note.assessment ||
      seeded.plan !== note.plan;

    if (regenerated) {
      lastSeededNote.current = note;
      setDraft(note);
    }
  }, [note]);
  const noteKey = `${note.subjective.length}-${note.objective.length}-${note.assessment.length}-${note.plan.length}`;

  const handleSign = () => {
    onSign();
    setConfetti(true);
  };

  const editorSections: { key: keyof typeof note; label: string }[] = [
    { key: 'subjective', label: 'Subjective' },
    { key: 'objective', label: 'Objective' },
    { key: 'assessment', label: 'Assessment' },
    { key: 'plan', label: 'Plan' },
  ];

  const sections: { title: string; html: string }[] = [
    { title: 'SUBJECTIVE', html: draft.subjective },
    { title: 'OBJECTIVE', html: draft.objective },
    { title: 'ASSESSMENT', html: draft.assessment },
    { title: 'PLAN', html: draft.plan },
  ];

  if (completed) {
    // Charting Completion experience, to the provided design (Kara,
    // 2026-09-28). Back to home is the single final action; the ✕ exits the
    // same way.
    return (
      <div className="relative overflow-hidden rounded-lg border border-gray-200 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 px-6 py-12">
        {COMPLETION_CONFETTI.map((piece, i) => (
          <span
            key={i}
            aria-hidden
            className={`pointer-events-none absolute rounded-sm ${piece}`}
          />
        ))}
        <button
          type="button"
          aria-label="Close"
          onClick={onExit}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          ✕
        </button>

        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg shadow-primary-600/30 ring-4 ring-primary-100 dark:ring-primary-900/40">
          <Check className="h-7 w-7" strokeWidth={3} aria-hidden />
        </div>
        <h2 className="mt-4 text-center text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
          {COMPLETION_SCREEN.titlePre}
          <span className="text-primary-600 dark:text-primary-400">
            {COMPLETION_SCREEN.titleEmphasis}
          </span>
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-sm text-gray-500 dark:text-gray-300">
          {COMPLETION_SCREEN.subtitle}
        </p>

        <div className="mx-auto mt-8 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3">
          {COMPLETION_SCREEN.cards.map((card, i) => (
            <div
              key={card.title}
              className="rounded-2xl border border-gray-200 bg-white px-5 py-5 text-center shadow-sm dark:border-dark-gray-600 dark:bg-dark-gray-800"
            >
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
                {COMPLETION_CARD_ICONS[i]}
              </span>
              <h3 className="mt-3 text-sm font-bold text-gray-900 dark:text-gray-100">
                {card.title}
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-300">
                {card.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-6 flex max-w-4xl items-center gap-4 rounded-xl bg-primary-50 px-5 py-4 ring-1 ring-inset ring-primary-300/70 dark:bg-primary-900/20 dark:ring-primary-800">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-primary-600 ring-2 ring-primary-500 dark:bg-dark-gray-800 dark:text-primary-300 dark:ring-primary-400">
            <Clock className="h-5 w-5" strokeWidth={2.25} aria-hidden />
          </span>
          <p className="border-l-2 border-primary-300 pl-4 text-sm text-gray-600 dark:border-primary-700 dark:text-gray-300">
            <span className="block font-bold text-gray-900 dark:text-gray-100">
              {COMPLETION_SCREEN.bannerLead}
            </span>
            {COMPLETION_SCREEN.bannerBody}
          </p>
        </div>

        {sandbox ? <WalkthroughScheduling /> : null}
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={onExit}
            className={
              sandbox
                ? 'inline-flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-900/20'
                : 'inline-flex items-center gap-2 rounded-lg bg-primary-600 px-7 py-3 text-sm font-semibold text-white shadow-md shadow-primary-600/30 transition-colors hover:bg-primary-700'
            }
          >
            <Home className="h-4 w-4" strokeWidth={2.5} aria-hidden />
            {sandbox ? 'Try the walkthrough again' : COMPLETION_SCREEN.cta}
          </button>
        </div>
        <p className="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">
          {COMPLETION_SCREEN.footer}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      {signed ? (
        <div className="flex-1 rounded-lg border border-gray-200 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800">
          <div className="border-b border-gray-200 dark:border-dark-gray-600 px-4 sm:px-6 py-4">
            <div className="text-center text-sm font-bold tracking-widest text-gray-900 dark:text-gray-100 underline">
              DAILY NOTE
            </div>
            <dl className="mt-3 space-y-0.5 text-sm text-gray-700 dark:text-gray-200">
              <div>
                <dt className="inline font-bold">PATIENT NAME: </dt>
                <dd className="inline">
                  {SANDBOX_PATIENT.fname} {SANDBOX_PATIENT.lname} (DOB{' '}
                  {dayjs(SANDBOX_PATIENT.dateOfBirth).format('MM/DD/YYYY')})
                </dd>
              </div>
              <div>
                <dt className="inline font-bold">CLINICIAN NAME: </dt>
                <dd className="inline">{me?.name || 'Your Provider'}</dd>
              </div>
              <div>
                <dt className="inline font-bold">ENCOUNTER DATE: </dt>
                <dd className="inline">
                  {dayjs(Number(visitForm.visitDate)).format('MM/DD/YYYY')}
                </dd>
              </div>
            </dl>
          </div>

          {/* One flowing document, scrolled by the page itself — no inner
              scroll region or pinned header (round-2 tester note #8). */}
          <div className="px-4 sm:px-6 py-4 space-y-5">
            {voiceNote ? (
              <section>
                <h3 className="text-sm font-bold tracking-wide text-gray-900 dark:text-gray-100 underline">
                  VOICENOTE SUMMARY - SUBJECTIVE:
                </h3>
                <div
                  className="prose prose-sm dark:prose-invert max-w-none mt-1 text-gray-800 dark:text-gray-100"
                  dangerouslySetInnerHTML={{ __html: voiceNote }}
                />
              </section>
            ) : null}
            {sections.map(section =>
              section.html ? (
                <section key={section.title}>
                  <h3 className="text-sm font-bold tracking-wide text-gray-900 dark:text-gray-100 underline">
                    {section.title}:
                  </h3>
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none mt-1 text-gray-800 dark:text-gray-100"
                    // Generated by our own note builders from sandbox data.
                    dangerouslySetInnerHTML={{ __html: section.html }}
                  />
                </section>
              ) : null
            )}
            {sections.every(section => !section.html) && (
              <p className="py-10 text-center text-sm text-gray-400">
                Nothing documented yet. Go back and make a few selections, then return to sign.
              </p>
            )}
          </div>
        </div>
      ) : (
        // The same editor stack the real SOAP step renders (SoapStepV2):
        // VoiceNote Summary first, then one labeled TiptapEditor per section,
        // fully editable, no Clear.
        <div key={noteKey} className="flex-1 min-w-0 space-y-6">
          <TiptapEditor
            macros
            html
            billingCodes={!sandbox}
            label="VoiceNote Summary - Subjective"
            value={voiceNote}
            onChange={(value: string) => setVoiceNote(value)}
            containerClassName="soap-note bg-transparent dark:bg-transparent [&_.ProseMirror]:bg-white dark:[&_.ProseMirror]:bg-dark-gray-700"
          />
          {editorSections.map(section => (
            <TiptapEditor
              key={section.key}
              macros
              html
              billingCodes={!sandbox}
              label={section.label}
              value={draft[section.key]}
              onChange={(value: string) => setDraft(prev => ({ ...prev, [section.key]: value }))}
              containerClassName="soap-note bg-transparent dark:bg-transparent [&_.ProseMirror]:bg-white dark:[&_.ProseMirror]:bg-dark-gray-700"
            />
          ))}
        </div>
      )}

      <div className="lg:w-80 shrink-0">
        {!signed ? (
          <WalkthroughCallout
            stepLabel={OAP_COPY.reviewSign.stepLabel}
            title={OAP_COPY.reviewSign.title}
            body={OAP_COPY.reviewSign.body}
            cta="Sign Note →"
            onCta={handleSign}
            onBack={onBack}
            className="lg:sticky lg:top-4"
          />
        ) : (
          // Post-sign the signed note stays visible and scrollable, with this
          // callout carrying the walkthrough forward — no blocking celebration
          // pop-up (round-2 tester note #6). Desktop only — mobile gets the
          // sticky bottom bar below, since this column lands beneath the whole
          // note there (round-2 tester note #7).
          <div className="hidden lg:block">
            <WalkthroughCallout
              stepLabel={OAP_COPY.reviewSign.stepLabel}
              title={COMPLETED_NOTE_CALLOUT.title}
              body={COMPLETED_NOTE_CALLOUT.body}
              cta={COMPLETED_NOTE_CALLOUT.cta}
              onCta={onContinue}
              className="lg:sticky lg:top-4"
            />
          </div>
        )}
      </div>

      {signed && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-dark-gray-600 dark:bg-dark-gray-800/95 lg:hidden">
          <button
            type="button"
            onClick={onContinue}
            className="w-full rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
          >
            {COMPLETED_NOTE_CALLOUT.cta}
          </button>
        </div>
      )}

      {confetti && <ConfettiBurst onDone={() => setConfetti(false)} />}
    </div>
  );
};

export default ReviewSignStep;
